import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useVacancyStore, MainTabId } from '../../store/vacancyStore';
import { getTabPath } from '../Navigation/MainTabNavigation';
import type { Position as ApiPosition } from '../../../generated-src/client/positions/api';
import { PositionStatusEnum } from '../../../generated-src/client/positions/api';
import { getPositionStatusDotClass } from '../../utils/vacancyStatus';
import { ReactComponent as TagIcon } from '../../assets/icons/tag-icon.svg';
import { ReactComponent as AvitoIcon } from '../../assets/icons/channels/avito.svg';

export interface VacancyItemData {
  id: string;
  title: string;
  status: 'active' | 'inactive';
}

interface SidebarVacancyItemProps {
  vacancy: ApiPosition;
}

const getTabIdFromPath = (pathname: string): MainTabId | null => {
  if (pathname.includes('/vacancy/edit-questions/')) return 'vacancy';
  if (pathname.startsWith('/recruiter/vacancy')) return 'vacancy';
  if (pathname.startsWith('/recruiter/search')) return 'search';
  if (pathname.startsWith('/recruiter/mailings')) return 'mailings';
  if (pathname.startsWith('/recruiter/messenger')) return 'dialogs';
  if (pathname.startsWith('/recruiter/interviews')) return 'scheduled-interviews';
  if (pathname.startsWith('/recruiter/reports')) return 'analytics';
  return null;
};

export function SidebarVacancyItem({ vacancy }: SidebarVacancyItemProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    selectedVacancyId, 
    setSelectedVacancyById,
    lastActiveTabId,
  } = useVacancyStore();
  const isActive = selectedVacancyId === vacancy.id;

  const isArchivedStatus = vacancy.status === PositionStatusEnum.ARCHIVED;

  const handleClick = () => {
    console.log('SidebarVacancyItem: Selecting vacancy:', vacancy.title, vacancy.id);
    
    const currentTabId = getTabIdFromPath(location.pathname);
    const targetTabId = currentTabId && currentTabId !== 'vacancy' ? currentTabId : lastActiveTabId;
    setSelectedVacancyById(vacancy.id);
    
    if (targetTabId && targetTabId !== 'vacancy') {
      navigate(getTabPath(targetTabId, vacancy.id));
    } else {
      navigate(`/recruiter/vacancy/${vacancy.id}`);
    }
  };

  const internalTag = (vacancy as any).internalTag;
  const hasHhLink = !!(vacancy as any).hhVacancyId;
  const hasAvitoLink = !!(vacancy as any).avitoVacancyId;

  const content = (
    <div className="flex flex-col gap-[2px] w-full">
      <div className="flex items-center gap-2 min-w-0">
        <div
          className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
            getPositionStatusDotClass(vacancy.status)
          }`}
        />
        <span
          data-testid="sidebar-vacancy-item-title"
          className={`min-w-0 text-[14px] font-medium truncate ${
            isArchivedStatus ? 'text-[var(--text-tertiary)]' : 'text-[var(--text-dark)]'
          }`}
        >
          {vacancy.title}
        </span>
        {hasHhLink && (
          <span data-testid="sidebar-vacancy-item-hh-badge" className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-600 text-white flex-shrink-0">hh</span>
        )}
        {hasAvitoLink && (
          <span
            data-testid="sidebar-vacancy-item-avito-badge"
            title="Привязана к Авито"
            aria-label="Привязана к Авито"
            className="inline-flex h-6 w-6 items-center justify-center flex-shrink-0"
          >
            <AvitoIcon className="h-5 w-5" aria-hidden="true" />
          </span>
        )}
      </div>
      {internalTag && (
        <div className="flex items-center gap-1" style={{ marginLeft: '14px' }}>
          <TagIcon className="w-[10px] h-[10px] flex-shrink-0 text-[#868C98]" />
          <span data-testid="sidebar-vacancy-item-tag" className="text-[12px] leading-[1.33] text-[#868C98]">
            {internalTag}
          </span>
        </div>
      )}
    </div>
  );

  const className = `flex items-start gap-2 py-2 -mx-2 px-2 rounded-lg cursor-pointer transition-colors ${
    isActive
      ? 'bg-[#F5F6F1]'
      : 'hover:bg-[#F0F0EB]'
  }`;

  return (
    <div
      data-testid="sidebar-vacancy-item"
      data-vacancy-id={vacancy.id}
      className={className}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}
    >
      {content}
    </div>
  );
}
